import { useState } from "react";
import { Trash2 } from "lucide-react";
import { makeId, saveRecord } from "../lib/db";
import { dateInputValue, localDateKey, nowIso } from "../lib/time";
import type { Decision, DecisionStatus } from "../types";
import { Button, Field, Modal } from "./ui";

export function DecisionEditor({
  decision,
  draft,
  onClose,
  onSaved,
  onDelete
}: {
  decision?: Decision;
  draft?: Partial<Decision>;
  onClose: () => void;
  onSaved: (decision: Decision) => void;
  onDelete?: (decision: Decision) => void;
}) {
  const initial = decision ?? draft;
  const [title, setTitle] = useState(initial?.title ?? "");
  const [decisionText, setDecisionText] = useState(initial?.decision ?? "");
  const [rationale, setRationale] = useState(initial?.rationale ?? "");
  const [assumptions, setAssumptions] = useState(initial?.assumptions ?? "");
  const [alternatives, setAlternatives] = useState(initial?.alternatives ?? "");
  const [expectedOutcome, setExpectedOutcome] = useState(initial?.expectedOutcome ?? "");
  const [reviewAt, setReviewAt] = useState(dateInputValue(initial?.reviewAt));
  const [status, setStatus] = useState<DecisionStatus>(initial?.status ?? "active");
  const [actualOutcome, setActualOutcome] = useState(initial?.actualOutcome ?? "");
  const [outcomeAssessment, setOutcomeAssessment] = useState<Decision["outcomeAssessment"]>(
    initial?.outcomeAssessment
  );
  const [error, setError] = useState("");

  async function save() {
    if (
      !title.trim() ||
      !decisionText.trim() ||
      !rationale.trim() ||
      !expectedOutcome.trim() ||
      !reviewAt
    ) {
      setError("决策必须包含问题、决定、理由、预期结果和复核日期。");
      return;
    }
    if (status === "reviewed" && !actualOutcome.trim()) {
      setError("标记为已复盘时，请写下实际结果。");
      return;
    }
    const timestamp = nowIso();
    const next: Decision = {
      id: decision?.id ?? makeId(),
      kind: "decision",
      title: title.trim(),
      body: decision?.body ?? draft?.body ?? "",
      createdAt: decision?.createdAt ?? timestamp,
      updatedAt: timestamp,
      revision: decision?.revision ?? 0,
      deletedAt: decision?.deletedAt,
      sourceIds: decision?.sourceIds ?? draft?.sourceIds,
      status,
      decision: decisionText.trim(),
      rationale: rationale.trim(),
      assumptions: assumptions.trim(),
      alternatives: alternatives.trim(),
      expectedOutcome: expectedOutcome.trim(),
      reviewAt,
      actualOutcome: actualOutcome.trim() || undefined,
      outcomeAssessment,
      reviewedAt: status === "reviewed" ? (decision?.reviewedAt ?? timestamp) : undefined
    };
    const saved = await saveRecord(next);
    onSaved(saved);
    onClose();
  }

  return (
    <Modal
      title={decision ? "决策与复盘" : "记录决策"}
      eyebrow="重要判断"
      onClose={onClose}
      size="large"
      footer={
        <>
          {decision && onDelete && (
            <Button type="button" variant="ghost" className="footer-delete" onClick={() => onDelete(decision)}>
              <Trash2 size={16} /> 删除
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="button" variant="primary" onClick={() => void save()}>
            保存
          </Button>
        </>
      }
    >
      <div className="editor-form">
        <div className="form-grid">
          <Field label="要决定的问题" required>
            <input
              className="input input-large"
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="例如：是否暂停华东低毛利渠道"
            />
          </Field>
          <Field label="状态">
            <select className="input" value={status} onChange={(event) => setStatus(event.target.value as DecisionStatus)}>
              <option value="active">待复盘</option>
              <option value="reviewed">已复盘</option>
              <option value="archived">已归档</option>
            </select>
          </Field>
        </div>
        <div className="form-grid">
          <Field label="最终决定" required>
            <textarea className="textarea" rows={4} value={decisionText} onChange={(event) => setDecisionText(event.target.value)} />
          </Field>
          <Field label="为什么这样定" required>
            <textarea className="textarea" rows={4} value={rationale} onChange={(event) => setRationale(event.target.value)} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="关键假设">
            <textarea className="textarea" rows={3} value={assumptions} onChange={(event) => setAssumptions(event.target.value)} />
          </Field>
          <Field label="考虑过但没有选择的方案">
            <textarea
              className="textarea"
              rows={3}
              value={alternatives}
              onChange={(event) => setAlternatives(event.target.value)}
            />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="预期结果" required>
            <textarea
              className="textarea"
              rows={3}
              value={expectedOutcome}
              onChange={(event) => setExpectedOutcome(event.target.value)}
            />
          </Field>
          <Field label="复核日期" required>
            <input
              className="input"
              type="date"
              min={decision ? undefined : localDateKey()}
              value={reviewAt}
              onChange={(event) => setReviewAt(event.target.value)}
            />
          </Field>
        </div>
        {status === "reviewed" && (
          <div className="review-result-panel">
            <h3>实际发生了什么？</h3>
            <Field label="实际结果" required>
              <textarea
                className="textarea"
                rows={4}
                value={actualOutcome}
                onChange={(event) => setActualOutcome(event.target.value)}
              />
            </Field>
            <Field label="与预期相比">
              <select
                className="input"
                value={outcomeAssessment ?? ""}
                onChange={(event) =>
                  setOutcomeAssessment((event.target.value || undefined) as Decision["outcomeAssessment"])
                }
              >
                <option value="">未评价</option>
                <option value="as_expected">基本符合预期</option>
                <option value="partly">部分符合</option>
                <option value="not_as_expected">偏离预期</option>
              </select>
            </Field>
          </div>
        )}
        {error && <p className="form-error">{error}</p>}
      </div>
    </Modal>
  );
}
